import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { RAIL_SECTIONS, RAIL_TAIL, canSeeNav } from '../lib/rail-nav';
import { accountScopeLabel, useAccountFilter, withMine } from '../lib/account-filter';
import type { RoleOrLocked } from '../lib/auth-types';

// ⌘K command palette — the role-agnostic fast path. Searches accounts (scoped,
// via /api/search) and jumps to any nav destination the user can see. Keyboard:
// ⌘K toggles (handled by RailShell), ↑/↓ move, ↵ selects, esc closes.

interface OrgHit {
  id: string;
  name: string | null;
  kind: string;
}
type Result =
  | { type: 'org'; id: string; name: string | null; kind: string }
  | { type: 'jump'; to: string; label: string };

const KIND_TONE: Record<string, string> = {
  customer: 'var(--success)',
  prospect: 'var(--text-dim)',
  churned: 'var(--danger)',
};

export default function CommandPalette({
  role,
  allowedModules,
  onClose,
}: {
  role: RoleOrLocked;
  allowedModules: string[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { mine, setMine, scoped: acctScoped, available } = useAccountFilter();
  const [q, setQ] = useState('');
  const [orgs, setOrgs] = useState<OrgHit[]>([]);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // All nav destinations the user can see (sections + tail + Home). The
  // palette includes paletteOnly leaves — it is the one surface that searches
  // every VIEW, not just rail-rendered pages. Each entry carries its keywords
  // (in-page tab names, synonyms) as a secondary haystack, so either
  // vocabulary — "Sources" or "channels" — finds the same destination.
  const jumps = useMemo(() => {
    const items: Array<{ to: string; label: string; kw: string; paletteOnly: boolean }> = [
      { to: '/home', label: 'Home', kw: '', paletteOnly: false },
    ];
    const push = (l: (typeof RAIL_TAIL)[number], label: string) =>
      items.push({
        to: l.to,
        label,
        kw: (l.keywords ?? []).join(' ').toLowerCase(),
        paletteOnly: !!l.paletteOnly,
      });
    RAIL_SECTIONS.forEach((s) =>
      s.leaves.forEach((l) => {
        if (canSeeNav(l, role, allowedModules)) push(l, `${s.label} · ${l.label}`);
      }),
    );
    RAIL_TAIL.forEach((l) => {
      if (canSeeNav(l, role, allowedModules)) push(l, l.label);
    });
    return items;
  }, [role, allowedModules]);

  const term = q.trim().toLowerCase();
  // Label matches rank ABOVE keyword-only matches — typing "cohorts" must
  // put the Cohorts page first, not a marketing view that merely lists
  // "cohorts" as a synonym. The empty-query default shows PAGES only
  // (paletteOnly view deep-links appear when searched for, not as filler).
  const filteredJumps = term
    ? [
        ...jumps.filter((j) => j.label.toLowerCase().includes(term)),
        ...jumps.filter((j) => !j.label.toLowerCase().includes(term) && j.kw.includes(term)),
      ].slice(0, 6)
    : jumps.filter((j) => !j.paletteOnly).slice(0, 6);

  // Debounced account search. Class A: it follows the app-wide "My accounts"
  // filter, so ⌘K searches your book by default and the whole customer base
  // once you clear the filter — the same rule the accounts list obeys.
  useEffect(() => {
    if (!q.trim()) {
      setOrgs([]);
      return;
    }
    const t = setTimeout(() => {
      apiFetch<{ orgs: OrgHit[] }>(
        withMine(`/api/search?q=${encodeURIComponent(q.trim())}`, mine),
      )
        .then((r) => setOrgs(r.orgs ?? []))
        .catch(() => setOrgs([]));
    }, 180);
    return () => clearTimeout(t);
  }, [q, mine]);

  // Jump-to first, then accounts — navigation leads (matches the mockup).
  const results: Result[] = [
    ...filteredJumps.map((j) => ({ type: 'jump' as const, to: j.to, label: j.label })),
    ...orgs.map((o) => ({ type: 'org' as const, id: o.id, name: o.name, kind: o.kind })),
  ];
  const firstOrgIdx = filteredJumps.length;

  useEffect(() => {
    setSel(0);
  }, [q, orgs.length]);

  const go = (r: Result | undefined) => {
    if (!r) return;
    onClose();
    navigate(r.type === 'org' ? `/orgs/${r.id}` : r.to);
  };

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="cmdk" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="cmdk__input"
          placeholder="Search accounts · jump to a section…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((s) => Math.min(s + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((s) => Math.max(s - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              go(results[sel]);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onClose();
            }
          }}
        />
        <div className="cmdk__results">
          {results.map((r, i) =>
            r.type === 'jump' ? (
              <RowJump
                key={`j-${r.to}-${i}`}
                label={r.label}
                home={r.label === 'Home'}
                selected={i === sel}
                first={i === 0}
                onHover={() => setSel(i)}
                onClick={() => go(r)}
              />
            ) : (
              <Fragment key={`o-${r.id}`}>
                {i === firstOrgIdx && (
                  <div className="cmdk__grp">
                    Accounts{' '}
                    <span style={{ textTransform: 'none', color: 'var(--text-dim)' }}>
                      {/* The hits change meaning with the filter, so the header
                          changes with them (AC-7b's rule). */}
                      · {acctScoped ? accountScopeLabel(mine) : 'all accounts'}
                    </span>
                  </div>
                )}
                <div
                  className={`cmdk__row ${i === sel ? 'cmdk__row--sel' : ''}`}
                  onMouseEnter={() => setSel(i)}
                  onClick={() => go(r)}
                >
                  <span className="cmdk__ic">▣</span>
                  <span className="cmdk__label">{r.name ?? r.id}</span>
                  <span className="cmdk__kind" style={{ color: KIND_TONE[r.kind] ?? 'var(--text-dim)' }}>
                    {r.kind}
                  </span>
                </div>
              </Fragment>
            ),
          )}
          {term && results.length === 0 && (
            <div className="cmdk__empty">
              No matches.
              {acctScoped && available && mine && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => setMine(false)}
                  >
                    Search all accounts
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="cmdk__foot">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>esc close</span>
          <span style={{ marginLeft: 'auto' }}>the role-agnostic fast path</span>
        </div>
      </div>
    </div>
  );
}

function RowJump({
  label,
  home,
  selected,
  first,
  onHover,
  onClick,
}: {
  label: string;
  home: boolean;
  selected: boolean;
  first: boolean;
  onHover: () => void;
  onClick: () => void;
}) {
  return (
    <>
      {first && <div className="cmdk__grp">Jump to</div>}
      <div
        className={`cmdk__row ${selected ? 'cmdk__row--sel' : ''}`}
        onMouseEnter={onHover}
        onClick={onClick}
      >
        <span className="cmdk__ic">{home ? '▸' : '◇'}</span>
        <span className="cmdk__label">{label}</span>
        <span className="cmdk__kind">{home ? 'home' : 'section'}</span>
      </div>
    </>
  );
}
